import { useEffect, useRef, useCallback, useState, memo } from "react";
import { useReducedMotion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface Orb {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  opacity: number;
  phase: number;
}

const AnimatedBackground = memo(() => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
  const orbsRef = useRef<Orb[]>([]);
  const rafRef = useRef<number>(0);
  const isVisibleRef = useRef(false);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const schedule = (cb: () => void) => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) return ric(cb, { timeout: 1500 });
      return window.setTimeout(cb, 200);
    };
    const id = schedule(() => setReady(true));
    return () => {
      const cic = (window as unknown as { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (cic) cic(id as number);
      else clearTimeout(id as number);
    };
  }, []);

  const initOrbs = useCallback((w: number, h: number) => {
    const colors = [
      "107,143,113",
      "76,110,82",
      "168,192,157",
      "240,238,233",
      "58,84,64",
    ];
    const orbs: Orb[] = [];
    // Reduced count: mobile 2, desktop max 4
    const count = isMobile ? 2 : Math.min(4, Math.max(2, Math.floor(w / 400)));
    for (let i = 0; i < count; i++) {
      orbs.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * (isMobile ? 0.12 : 0.4),
        vy: (Math.random() - 0.5) * (isMobile ? 0.12 : 0.4),
        radius: isMobile ? Math.random() * 120 + 80 : Math.random() * 250 + 150,
        color: colors[i % colors.length],
        opacity: isMobile ? Math.random() * 0.15 + 0.08 : Math.random() * 0.21 + 0.10,
        phase: Math.random() * Math.PI * 2,
      });
    }
    orbsRef.current = orbs;
  }, [isMobile]);

  useEffect(() => {
    if (prefersReducedMotion) return;
    if (!ready) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let w = 0;
    let h = 0;

    const resize = () => {
      const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
      const parent = canvas.parentElement;
      if (!parent) return;
      w = parent.clientWidth;
      h = parent.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (orbsRef.current.length === 0) initOrbs(w, h);
    };

    resize();
    window.addEventListener("resize", resize);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = isVisibleRef.current;
        isVisibleRef.current = entry.isIntersecting;
        if (!wasVisible && entry.isIntersecting) {
          rafRef.current = requestAnimationFrame(animate);
        }
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    // Desktop only: mouse tracking (throttled)
    let lastMouseTime = 0;
    const onMouseMove = isMobile ? null : (e: MouseEvent) => {
      const now = performance.now();
      if (now - lastMouseTime < 50) return; // throttle to ~20fps
      lastMouseTime = now;
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        active: true,
      };
    };

    const onMouseLeave = isMobile ? null : () => {
      mouseRef.current.active = false;
    };

    if (onMouseMove) canvas.addEventListener("mousemove", onMouseMove, { passive: true });
    if (onMouseLeave) canvas.addEventListener("mouseleave", onMouseLeave);

    let time = 0;
    let lastFrame = 0;
    // Throttle: mobile ~24fps, desktop ~30fps
    const frameInterval = isMobile ? 42 : 33;

    const animate = (now: number) => {
      if (!isVisibleRef.current) return;

      if (now - lastFrame < frameInterval) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }
      lastFrame = now;

      time += 0.003;
      ctx.clearRect(0, 0, w, h);

      const mouse = mouseRef.current;
      const maxSpeed = isMobile ? 0.3 : 0.6;

      for (let i = 0; i < orbsRef.current.length; i++) {
        const orb = orbsRef.current[i];
        orb.x += orb.vx;
        orb.y += orb.vy;

        orb.vx += Math.sin(time * 0.7 + orb.phase) * 0.01;
        orb.vy += Math.cos(time * 0.5 + orb.phase * 1.3) * 0.01;
        const speed = Math.sqrt(orb.vx * orb.vx + orb.vy * orb.vy);
        if (speed > maxSpeed) { orb.vx *= 0.97; orb.vy *= 0.97; }

        const breathe = Math.sin(time * 0.8 + orb.phase) * 0.15 + 1;

        if (!isMobile && mouse.active) {
          const mx = mouse.x * w;
          const my = mouse.y * h;
          const dx = mx - orb.x;
          const dy = my - orb.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 350) {
            const force = (1 - dist / 350) * 0.2;
            orb.x += dx * force * 0.005;
            orb.y += dy * force * 0.005;
          }
        }

        if (orb.x < -orb.radius) orb.x = w + orb.radius;
        if (orb.x > w + orb.radius) orb.x = -orb.radius;
        if (orb.y < -orb.radius) orb.y = h + orb.radius;
        if (orb.y > h + orb.radius) orb.y = -orb.radius;

        const r = orb.radius * breathe;
        const gradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, r);
        gradient.addColorStop(0, `rgba(${orb.color}, ${orb.opacity * 1.6})`);
        gradient.addColorStop(0.5, `rgba(${orb.color}, ${orb.opacity * 0.5})`);
        gradient.addColorStop(1, `rgba(${orb.color}, 0)`);

        ctx.beginPath();
        ctx.arc(orb.x, orb.y, r, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    if (isVisibleRef.current) {
      rafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      window.removeEventListener("resize", resize);
      if (onMouseMove) canvas.removeEventListener("mousemove", onMouseMove);
      if (onMouseLeave) canvas.removeEventListener("mouseleave", onMouseLeave);
    };
  }, [prefersReducedMotion, isMobile, initOrbs, ready]);

  if (prefersReducedMotion) {
    return (
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-forest/5 blur-[120px]" />
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-auto"
    />
  );
});

AnimatedBackground.displayName = "AnimatedBackground";

export default AnimatedBackground;
